import { Component, ChangeDetectionStrategy, input, computed, OnInit, DestroyRef, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule, MAT_SELECT_CONFIG } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { CountryCode } from 'libphonenumber-js';
import {
  CountryCodeService,
  CountryOption,
} from '@vendasta/galaxy/utility/country-codes';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Observable, map, startWith, withLatestFrom, distinctUntilChanged, shareReplay, switchMap } from 'rxjs';
import { stopChromeAutofill } from '@vendasta/galaxy/filter/chips/src/filter-inputs/stop-chrome-autofill';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { DEFAULT_COUNTRY_CODE, validatePhone } from '@vendasta/galaxy/utility/phone-utils';

@Component({
  selector: 'sms-phone-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './sms-phone-input.component.html',
  styleUrls: ['./sms-phone-input.component.scss'],
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    DigitOnlyModule,
    GalaxyFormFieldModule,
    TranslateModule,
    MatIconModule,
    GalaxyTooltipModule,
  ],
  providers: [
    CountryCodeService,
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'phone-input-country-selector-overlay-pane' },
    },
  ],
})
export class SmsPhoneInputComponent implements OnInit {
  formGroup = input.required<FormGroup<PhoneInputControls>>();
  label = input<string>();
  hint = input<string>();
  tooltip = input<string>();
  required = input(false);

  private readonly countryCodeService = inject(CountryCodeService);
  private readonly destroyRef = inject(DestroyRef);

  searchCountryControl = new FormControl('');
  disableAutoComplete: string = stopChromeAutofill();

  private readonly formGroupValueChanges$ = toObservable(this.formGroup).pipe(
    switchMap((formGroup) => formGroup.valueChanges.pipe(startWith(formGroup.value))),
    takeUntilDestroyed(this.destroyRef),
  );

  private readonly formValue = toSignal(this.formGroupValueChanges$, {
    initialValue: { country: DEFAULT_COUNTRY_CODE, phoneNumber: '' },
  });

  readonly selectedCountry = computed(() => {
    const countryCode = this.formValue()?.country || DEFAULT_COUNTRY_CODE;
    return this.countryCodeService.getCountryOption(countryCode as CountryCode);
  });

  readonly selectedCountryCallingCode = computed(() => this.selectedCountry()?.callingCode || '');

  readonly countryOptions$: Observable<CountryOption[]> = this.countryCodeService.countryOptions$.pipe(
    distinctUntilChanged(),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly filteredCountryOptions$: Observable<CountryOption[]> = this.searchCountryControl.valueChanges.pipe(
    startWith(''),
    withLatestFrom(this.countryOptions$),
    map(([filterValue, countryOptions]) => {
      const fVal = filterValue || '';
      return countryOptions.filter((c) => {
        return (
          c.callingCode.includes(fVal) ||
          c.countryCode.toLowerCase().includes(fVal?.toLowerCase()) ||
          c.countryName.toLowerCase().includes(fVal?.toLowerCase())
        );
      });
    }),
  );

  ngOnInit(): void {
    this.countryCodeService.countryCodeUsed('CA');
    this.countryCodeService.countryCodeUsed(DEFAULT_COUNTRY_CODE);
  }

  onBlur(): void {
    this.formGroup().markAsTouched();
  }
}

export type PhoneInputControls = {
  country: FormControl<string>;
  phoneNumber: FormControl<string>;
  extension?: FormControl<string>;
};

export function validatePhoneInputFormGroup(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  if (!value?.phoneNumber) {
    return null;
  }

  const phoneNumber = value.phoneNumber;
  const country = (value.country || DEFAULT_COUNTRY_CODE) satisfies CountryCode;

  return validatePhone(phoneNumber, country);
}

export function createPhoneInputFormGroup(
  initialValue?: { country?: string; phoneNumber?: string; extension?: string },
  includeExtension = true,
): FormGroup<PhoneInputControls> {
  const controls: PhoneInputControls = {
    country: new FormControl(initialValue?.country || DEFAULT_COUNTRY_CODE, { nonNullable: true }),
    phoneNumber: new FormControl(initialValue?.phoneNumber || '', { nonNullable: true }),
  };

  if (includeExtension) {
    controls.extension = new FormControl(initialValue?.extension || '', { nonNullable: true });
  }

  return new FormGroup<PhoneInputControls>(controls, { validators: [validatePhoneInputFormGroup] });
}
